import { useMemo, useState } from "react";

import { CONFLICTO, MUY_GRANDE, restaurarVersion, traerVersion, versionesDePlanilla } from "../services/espacio";
import { diaHoraAr } from "../shared/fechaAr";
import { colALetra, evaluar } from "../shared/planilla";
import "./css/espacio-historial.css";
import { TOPE_PLANILLA_MB } from "../shared/topes";

/* El historial de versiones de una planilla de FiboSheets.
 *
 * Las versiones las saca la BASE sola (un trigger al cambiar el contenido,
 * como mucho una cada 10 minutos, también cuando escribe una automatización):
 * acá sólo se listan, se miran y —el dueño— se restauran. Cada versión es
 * cómo estaba la planilla en ese momento; la actual es la planilla misma.
 *
 * Vive en su propio archivo (y no adentro de MiEspacio.jsx) para que el editor
 * sólo tenga que ponerlo y pasarle dos cosas:
 *  · `prepararRestaurar()`: guarda lo pendiente y devuelve
 *    `{ nombre, actualizadoEn }` —el nombre de ahora y la versión que la
 *    pestaña tiene en la mano— o null si no se pudo guardar (conflicto, sin
 *    internet). Sin esto, restaurar pisaría las últimas teclas o chocaría con
 *    el propio autoguardado.
 *  · `onRestaurada()`: el editor relee la planilla de la base.
 *
 * Todo se pide recién al abrir el panel: abrir una planilla no cuesta una
 * consulta más.
 */

/* Quién dejó la planilla así: el mail, «una automatización» si fue el bot, o
   nada si no se sabe (las fotos de antes de que existiera la marca). */
export function quienGuardo(guardadaPor) {
  if (!guardadaPor) return "—";
  if (guardadaPor === "bot") return "una automatización";
  return guardadaPor;
}

/* Hasta dónde se dibuja una versión. Una hoja de 5000 filas en una tabla común
   cuelga el navegador; para mirar qué había alcanza con el principio. */
const FILAS_MAX = 300;
const COLS_MAX = 52;

/* Las hojas de un `datos` guardado, con lo justo para calcular y mirar. Una
   planilla vieja (un solo grid, sin `hojas`) es «Hoja 1», como la abre el
   editor. */
export function hojasDeDatos(datos) {
  const d = datos || {};
  const celdasDe = (h) => (Array.isArray(h && h.celdas) ? h.celdas : [])
    .map((f) => Array.from(f || [], (x) => (x == null ? "" : String(x))));
  if (Array.isArray(d.hojas) && d.hojas.length) {
    return d.hojas.map((h, i) => ({ nombre: (h && h.nombre) || `Hoja ${i + 1}`, celdas: celdasDe(h) }));
  }
  return [{ nombre: "Hoja 1", celdas: celdasDe(d) }];
}

/* El rectángulo que tiene algo escrito (las hojas nacen de 100×26 vacías). */
function usado(celdas) {
  let filas = 0;
  let cols = 0;
  celdas.forEach((f, r) => {
    f.forEach((x, c) => {
      if (x !== "") { filas = Math.max(filas, r + 1); cols = Math.max(cols, c + 1); }
    });
  });
  return { filas, cols };
}

const mostrar = (v) => {
  if (v == null) return "";
  if (typeof v === "number") return v.toLocaleString("es-AR", { maximumFractionDigits: 10 });
  if (typeof v === "boolean") return v ? "VERDADERO" : "FALSO";
  return String(v);
};

/* Una versión en SOLO LECTURA: las fórmulas calculadas (sin los datos del hub,
   que son de ahora y no de esa fecha) y sin formato. Es para ver qué había,
   no para trabajar: para eso está «Restaurar». */
export function VistaVersion({ version, esDueno, trabajando, onRestaurar, onCerrar }) {
  const hojas = useMemo(() => hojasDeDatos(version.datos), [version]);
  const [activa, setActiva] = useState(0);
  const hoja = hojas[activa] || hojas[0];
  const valores = useMemo(
    () => (hoja ? evaluar(hoja.celdas, { hojas, hojaActual: hoja.nombre }) : []),
    [hoja, hojas],
  );
  const { filas, cols } = usado(hoja ? hoja.celdas : []);
  const verFilas = Math.min(filas, FILAS_MAX);
  const verCols = Math.min(cols, COLS_MAX);

  return (
    <div className="esph-vista" role="region" aria-label="Versión en solo lectura">
      <div className="esph-vista-cabeza">
        <button className="esp-btn" onClick={onCerrar}>← Volver al historial</button>
        <span>
          Así estaba <b>{version.nombre || "Sin título"}</b> el {diaHoraAr(version.guardada_en)}
          {" "}<span className="tenue">({quienGuardo(version.guardada_por)}) · solo lectura</span>
        </span>
        {esDueno && (
          <button className="btn-primario" disabled={!!trabajando} onClick={onRestaurar}>
            Restaurar esta versión
          </button>
        )}
      </div>

      {hojas.length > 1 && (
        <div className="esph-hojas" role="tablist">
          {hojas.map((h, i) => (
            <button
              key={i} role="tab" aria-selected={i === activa}
              className={i === activa ? "esp-btn esph-hoja activa" : "esp-btn esph-hoja"}
              onClick={() => setActiva(i)}
            >{h.nombre}</button>
          ))}
        </div>
      )}

      {verFilas === 0 ? (
        <p className="tenue">Esta hoja estaba vacía.</p>
      ) : (
        <div className="esph-tabla-wrap">
          <table className="esph-tabla">
            <thead>
              <tr>
                <th />
                {Array.from({ length: verCols }, (_, c) => <th key={c}>{colALetra(c)}</th>)}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: verFilas }, (_, r) => (
                <tr key={r}>
                  <th>{r + 1}</th>
                  {Array.from({ length: verCols }, (_, c) => {
                    const v = valores[r] ? valores[r][c] : "";
                    return (
                      <td key={c} className={typeof v === "number" ? "num" : undefined}>{mostrar(v)}</td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {(filas > FILAS_MAX || cols > COLS_MAX) && (
        <p className="tenue">
          Se muestran las primeras {verFilas} filas y {verCols} columnas. Para verla
          entera, restaurala (lo de ahora queda guardado en el historial).
        </p>
      )}
    </div>
  );
}

const ERROR_RESTAURAR = {
  [CONFLICTO]: "La planilla cambió en otro lado mientras restaurabas (otra pestaña o una automatización): no se pisó nada. Recargá y probá de nuevo.",
  [MUY_GRANDE]: `Esa versión pasa el tope de ${TOPE_PLANILLA_MB} MB y no se puede volver a guardar.`,
};

export default function HistorialPlanilla({ planillaId, esDueno, prepararRestaurar, onRestaurada }) {
  const [abierto, setAbierto] = useState(false);
  const [versiones, setVersiones] = useState(null);
  const [errorLista, setErrorLista] = useState(null);
  const [viendo, setViendo] = useState(null); // la versión entera, abierta en solo lectura
  const [trabajando, setTrabajando] = useState(null); // id de la que se abre o se restaura
  const [aviso, setAviso] = useState(null);

  const abrir = () => {
    setAbierto(true);
    setViendo(null);
    setAviso(null);
    setErrorLista(null);
    setVersiones(null);
    versionesDePlanilla(planillaId)
      .then(setVersiones)
      .catch(() => setErrorLista("No se pudo consultar."));
  };
  const cerrar = () => { if (!trabajando) setAbierto(false); };

  const ver = async (v) => {
    setTrabajando(v.id);
    setAviso(null);
    try {
      setViendo(await traerVersion(v.id));
    } catch {
      setAviso("No se pudo abrir esa versión.");
    } finally {
      setTrabajando(null);
    }
  };

  const restaurar = async (v) => {
    const cuando = diaHoraAr(v.guardada_en);
    if (!window.confirm(
      `¿Volver la planilla a como estaba el ${cuando}? Lo de ahora queda guardado en el historial: si te arrepentís, lo restaurás.`,
    )) return;
    setTrabajando(v.id);
    setAviso(null);
    try {
      const listo = await prepararRestaurar();
      if (!listo) {
        setAviso("Hay cambios que no se pudieron guardar (mirá el cartel de la planilla). Resolvé eso primero y después restaurá.");
        return;
      }
      await restaurarVersion(planillaId, v.id, listo);
      setAbierto(false);
      setViendo(null);
      onRestaurada();
    } catch (e) {
      setAviso(ERROR_RESTAURAR[e && e.codigo] || "No se pudo restaurar. Probá de nuevo en un rato.");
    } finally {
      setTrabajando(null);
    }
  };

  return (
    <>
      <button className="esp-btn" onClick={abrir} title="Las versiones anteriores de esta planilla">
        🕘 Historial
      </button>
      {abierto && (
        <div className="esph-fondo" onClick={cerrar} role="presentation">
          <div
            className={viendo ? "esph-panel ancho" : "esph-panel"}
            onClick={(e) => e.stopPropagation()}
            role="dialog" aria-label="Historial de la planilla"
          >
            {viendo ? (
              <VistaVersion
                version={viendo} esDueno={esDueno} trabajando={trabajando}
                onRestaurar={() => restaurar(viendo)} onCerrar={() => setViendo(null)}
              />
            ) : (
              <>
                <div className="esph-cabeza">
                  <h3>Historial</h3>
                  <button className="esp-btn chico" onClick={cerrar} aria-label="Cerrar el historial">×</button>
                </div>
                {aviso && <p className="panel-aviso esph-aviso" role="alert">{aviso}</p>}
                {errorLista && <p className="panel-aviso" role="alert">{errorLista}</p>}
                {!errorLista && versiones === null && <p className="tenue">Cargando…</p>}
                {versiones && versiones.length === 0 && (
                  <p className="tenue">
                    Todavía no hay versiones guardadas. La primera aparece la
                    próxima vez que se cambie la planilla.
                  </p>
                )}
                {versiones && versiones.length > 0 && (
                  <ul className="esph-lista">
                    {versiones.map((v) => (
                      <li key={v.id}>
                        <span className="esph-cuando">
                          <b>{diaHoraAr(v.guardada_en)}</b>
                          <span className="tenue">{quienGuardo(v.guardada_por)}</span>
                        </span>
                        <span className="esph-botones">
                          <button
                            className="esp-btn chico" disabled={!!trabajando} onClick={() => ver(v)}
                            aria-label={`Ver la versión del ${diaHoraAr(v.guardada_en)}`}
                          >Ver</button>
                          {esDueno && (
                            <button
                              className="esp-btn chico" disabled={!!trabajando} onClick={() => restaurar(v)}
                              aria-label={`Restaurar la versión del ${diaHoraAr(v.guardada_en)}`}
                            >Restaurar</button>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
            {viendo && aviso && <p className="panel-aviso esph-aviso" role="alert">{aviso}</p>}
          </div>
        </div>
      )}
    </>
  );
}
